import { type ProposalFlowType } from '#app/features/paidProjects/components/Proposal/useProposalFlow/useProposalFlow.js';
import { ModalTemplateWrapper } from '../ModalTemplateWrapper/ModalTemplateWrapper.js';
import { type CommonTemplateProps } from '../types.js';
import { useCallback } from 'react';

type AbandonEditableFlowModalProps = CommonTemplateProps & {
  readonly draftTermId?: ProposalFlowType['state']['formState']['draftTermId'];
  readonly hasChanged: ProposalFlowType['state']['formState']['hasChanged'];
  readonly onCancel: () => void;
  readonly onConfirmExit: () => void;
  readonly saveDraftProposal: () => void;
};

export const AbandonEditableFlowModal = ({
  closeModal,
  draftTermId,
  hasChanged,
  isOpen,
  onCancel,
  onConfirmExit,
  saveDraftProposal,
}: AbandonEditableFlowModalProps) => {
  const handleCancel = useCallback(() => {
    onCancel();
    closeModal();
  }, [closeModal, onCancel]);

  const handleConfirmExit = useCallback(() => {
    onConfirmExit();
    closeModal();
  }, [closeModal, onConfirmExit]);

  // saveDraftProposal
  const saveDraft = useCallback(() => {
    closeModal();
    saveDraftProposal();
  }, [closeModal, saveDraftProposal]);

  if (draftTermId) {
    if (!hasChanged) {
      handleConfirmExit();
      return null;
    }

    return (
      <ModalTemplateWrapper
        ariaLabel="Exit without saving?"
        ctaOptions={{
          primaryCTA: {
            handleOnClick: handleConfirmExit,
            label: 'Discard updates',
            variant: 'V2Destructive',
          },
          secondaryCTA: {
            handleOnClick: saveDraft,
            label: 'Save draft updates',
            variant: 'V2Primary' as unknown as undefined,
          },
        }}
        desktopModalSize="md"
        headerText="Save your changes?"
        isOpen={isOpen}
        onClose={handleCancel}
        subheaderText="Do you want to save your recent changes?"
      />
    );
  } else {
    return (
      <ModalTemplateWrapper
        ariaLabel="Exit without saving?"
        ctaOptions={{
          primaryCTA: {
            handleOnClick: handleConfirmExit,
            label: 'Continue without saving',
            variant: 'V2Secondary' as unknown as undefined,
          },
          secondaryCTA: {
            handleOnClick: saveDraft,
            label: 'Save as draft',
            variant: 'V2Primary' as unknown as undefined,
          },
        }}
        desktopModalSize="md"
        headerText="Save as a draft?"
        isOpen={isOpen}
        onClose={handleCancel}
        subheaderText="Do you want to save this proposal as draft?"
      />
    );
  }
};
